/**
 * providerJourneyMeta.ts — CLIENT mirror of the server's provider-journey
 * registry (functions/src/composio/providerJourneys.ts). Only the data the
 * client needs to route flows: which toolkits have an email connect journey
 * (intent capture → coached sign-in → post-connect verification).
 *
 * A lockstep test pins this list against the server registry — extend BOTH
 * when a provider joins the journey (the shared-package build is the
 * documented follow-up, same as the formatting doctrine's client floor).
 */

export const EMAIL_JOURNEY_SLUGS = ['outlook', 'gmail'] as const;

export function isEmailJourneySlug(slug: string): boolean {
  return (EMAIL_JOURNEY_SLUGS as readonly string[]).includes(
    String(slug || '').trim().toLowerCase(),
  );
}

/**
 * Derive the tenant-wide Microsoft admin-consent URL from the minted OAuth
 * authorize URL (C3b). Only possible when the redirect is a DIRECT
 * login.microsoftonline.com authorize URL carrying client_id — an opaque
 * Composio hop yields null and the journey degrades to instructions-only
 * (the documented fallback). Always the `organizations` endpoint, NEVER
 * `common` (the admin must approve THE APP for the tenant, not sign in as
 * the identity — the exact trap in the Fernando incident).
 */
export function deriveMicrosoftAdminConsentUrl(authorizeUrl: string | null | undefined): string | null {
  if (!authorizeUrl) return null;
  try {
    const parsed = new URL(String(authorizeUrl));
    if (!/(^|\.)login\.microsoftonline\.com$/i.test(parsed.hostname)) return null;
    const clientId = parsed.searchParams.get('client_id');
    if (!clientId || !/^[0-9a-f-]{8,64}$/i.test(clientId)) return null;
    return `https://login.microsoftonline.com/organizations/adminconsent?client_id=${encodeURIComponent(clientId)}`;
  } catch {
    return null;
  }
}
